import { useRouteError, isRouteErrorResponse, useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, Home, RefreshCw, ArrowLeft, WifiOff, FileQuestion } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { captureException, trackEvent } from '@/lib/posthog';
import Bugsnag from '@bugsnag/js';
import { useEffect } from 'react';
import {
  isChunkLoadError,
  handleChunkLoadError,
} from '@/lib/errorHandling/chunkErrorHandler';

interface ErrorDisplayProps {
  title: string;
  message: string;
  icon: React.ReactNode;
  showHomeButton?: boolean;
  showRetryButton?: boolean;
  showBackButton?: boolean;
}

function ErrorDisplay({
  title,
  message,
  icon,
  showHomeButton = true,
  showRetryButton = true,
  showBackButton = false,
}: ErrorDisplayProps) {
  const navigate = useNavigate();

  return (
    <div className="min-h-dvh bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center text-center">
            <div className="rounded-full bg-destructive/10 p-4 mb-6">
              {icon}
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-2">{title}</h1>
            <p className="text-slate-600 mb-6">{message}</p>

            <div className="flex flex-wrap gap-3 justify-center">
              {showRetryButton && (
                <Button onClick={() => window.location.reload()} variant="default">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              )}
              {showBackButton && (
                <Button onClick={() => navigate(-1)} variant="outline">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Go Back
                </Button>
              )}
              {showHomeButton && (
                <Button asChild variant="outline">
                  <Link to="/">
                    <Home className="h-4 w-4 mr-2" />
                    Home
                  </Link>
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Helper to handle chunk errors and track them
function handleChunkErrorWithTracking(error: Error, source: string): boolean {
  // Track this specific error type for analytics
  trackEvent('chunk_load_error', {
    error_message: error.message,
    url: window.location.href,
    source,
  });

  // Use shared handler for consistent behavior
  return handleChunkLoadError();
}

export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  useEffect(() => {
    // Track error in analytics
    if (isRouteErrorResponse(error)) {
      trackEvent('route_error', {
        status: error.status,
        statusText: error.statusText,
        url: window.location.href,
      });
    } else if (error instanceof Error) {
      captureException(error, {
        source: 'route_error_boundary',
        url: window.location.href,
      });
      Bugsnag.notify(error, (event) => {
        event.context = 'RouteErrorBoundary';
        event.addMetadata('route', { url: window.location.href });
      });
      trackEvent('route_error', {
        error_name: error.name,
        error_message: error.message,
        url: window.location.href,
      });
    }
  }, [error]);

  // Handle React Router error responses (404, etc.)
  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      return (
        <ErrorDisplay
          title="Page Not Found"
          message="The page you're looking for doesn't exist or has been moved."
          icon={<FileQuestion className="h-12 w-12 text-slate-400" />}
          showBackButton
          showRetryButton={false}
        />
      );
    }

    if (error.status === 401) {
      return (
        <ErrorDisplay
          title="Unauthorized"
          message="You need to sign in to access this page."
          icon={<AlertTriangle className="h-12 w-12 text-amber-500" />}
          showHomeButton
          showRetryButton={false}
        />
      );
    }

    if (error.status === 403) {
      return (
        <ErrorDisplay
          title="Access Denied"
          message="You don't have permission to view this page."
          icon={<AlertTriangle className="h-12 w-12 text-amber-500" />}
          showBackButton
          showRetryButton={false}
        />
      );
    }

    if (error.status >= 500) {
      return (
        <ErrorDisplay
          title="Server Error"
          message="Something went wrong on our end. Please try again in a few moments."
          icon={<AlertTriangle className="h-12 w-12 text-destructive" />}
        />
      );
    }

    return (
      <ErrorDisplay
        title={`Error ${error.status}`}
        message={error.statusText || 'An unexpected error occurred.'}
        icon={<AlertTriangle className="h-12 w-12 text-destructive" />}
      />
    );
  }

  // Handle network errors
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return (
      <ErrorDisplay
        title="Connection Problem"
        message="Unable to connect to the server. Please check your internet connection and try again."
        icon={<WifiOff className="h-12 w-12 text-slate-400" />}
      />
    );
  }

  // Handle chunk loading errors (code splitting failures)
  // This happens when users have cached HTML from a previous deploy
  if (isChunkLoadError(error)) {
    const isHandling = handleChunkErrorWithTracking(error as Error, 'route_error_boundary');
    if (isHandling) {
      return null; // Page is reloading
    }

    // If we already tried refreshing, show the error message
    return (
      <ErrorDisplay
        title="Loading Error"
        message="There was a problem loading this page. This sometimes happens after an update. Please refresh to get the latest version."
        icon={<RefreshCw className="h-12 w-12 text-slate-400" />}
        showRetryButton
        showBackButton={false}
      />
    );
  }

  // Generic error fallback
  const errorMessage =
    error instanceof Error ? error.message : 'An unexpected error occurred.';

  // Show stack trace in development
  const isDev = import.meta.env.DEV;

  return (
    <div className="min-h-dvh bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center text-center">
            <div className="rounded-full bg-destructive/10 p-4 mb-6">
              <AlertTriangle className="h-12 w-12 text-destructive" />
            </div>

            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Something Went Wrong
            </h1>
            <p className="text-slate-600 mb-4">
              We've encountered an unexpected error. Our team has been notified.
            </p>

            {isDev && error instanceof Error && (
              <div className="w-full mb-6 p-4 bg-slate-100 rounded-lg text-left overflow-auto">
                <p className="font-mono text-sm text-destructive mb-2 font-semibold">
                  {error.name}: {error.message}
                </p>
                {error.stack && (
                  <pre className="text-xs text-slate-600 overflow-x-auto whitespace-pre-wrap">
                    {error.stack.split('\n').slice(1, 6).join('\n')}
                  </pre>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button onClick={() => window.location.reload()} variant="default">
                <RefreshCw className="h-4 w-4 mr-2" />
                Reload Page
              </Button>
              <Button onClick={() => navigate(-1)} variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Go Back
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Simpler error boundary for nested routes (within layouts)
export function NestedRouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();

  useEffect(() => {
    // CRITICAL: Log the actual error for debugging
    console.error('[NestedRouteErrorBoundary] Caught error:', error);
    console.error('[NestedRouteErrorBoundary] Error type:', typeof error);
    console.error('[NestedRouteErrorBoundary] URL:', window.location.href);

    if (error instanceof Error) {
      console.error('[NestedRouteErrorBoundary] Error name:', error.name);
      console.error('[NestedRouteErrorBoundary] Error message:', error.message);
      console.error('[NestedRouteErrorBoundary] Error stack:', error.stack);

      captureException(error, {
        source: 'nested_route_error_boundary',
        url: window.location.href,
        errorName: error.name,
        errorMessage: error.message,
      });
      Bugsnag.notify(error, (event) => {
        event.context = 'NestedRouteErrorBoundary';
        event.addMetadata('route', { url: window.location.href });
      });
    } else if (error && typeof error === 'object') {
      // React Router may wrap errors in a Response or other object
      const errorObj = error as Record<string, unknown>;
      console.error('[NestedRouteErrorBoundary] Error object keys:', Object.keys(errorObj));
      console.error('[NestedRouteErrorBoundary] Error data:', errorObj.data);
      console.error('[NestedRouteErrorBoundary] Error status:', errorObj.status);
      console.error('[NestedRouteErrorBoundary] Error statusText:', errorObj.statusText);
    }
  }, [error]);

  // Handle chunk loading errors with auto-refresh
  if (isChunkLoadError(error)) {
    const isHandling = handleChunkErrorWithTracking(error as Error, 'nested_route_error_boundary');
    if (isHandling) {
      return null; // Page is reloading
    }

    // Already tried refreshing, show specific error message
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
        <div className="rounded-full bg-slate-100 p-4 mb-6">
          <RefreshCw className="h-10 w-10 text-slate-400" />
        </div>

        <h2 className="text-xl font-semibold text-slate-900 mb-2">
          Loading Error
        </h2>
        <p className="text-slate-600 mb-6 max-w-md">
          There was a problem loading this page. This sometimes happens after an update. Please refresh to get the latest version.
        </p>

        <div className="flex gap-3">
          <Button onClick={() => window.location.reload()} size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh Page
          </Button>
          <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm">
            <Home className="h-4 w-4 mr-2" />
            Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
      <div className="rounded-full bg-destructive/10 p-4 mb-6">
        <AlertTriangle className="h-10 w-10 text-destructive" />
      </div>

      <h2 className="text-xl font-semibold text-slate-900 mb-2">
        Unable to Load This Section
      </h2>
      <p className="text-slate-600 mb-6 max-w-md">
        There was a problem loading this content. Please try again.
      </p>

      <div className="flex gap-3">
        <Button onClick={() => window.location.reload()} size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
        <Button onClick={() => navigate('/dashboard')} variant="outline" size="sm">
          <Home className="h-4 w-4 mr-2" />
          Dashboard
        </Button>
      </div>
    </div>
  );
}

// 404 Not Found component for catch-all route
export function NotFoundPage() {
  return (
    <ErrorDisplay
      title="Page Not Found"
      message="The page you're looking for doesn't exist or has been moved."
      icon={<FileQuestion className="h-12 w-12 text-slate-400" />}
      showBackButton
      showRetryButton={false}
    />
  );
}

export default RouteErrorBoundary;
